import React, { useState } from 'react';
/**
 * `VrnInputModal` prompts the user to enter or update a vehicle registration
 * number (VRN) in a modal dialog.
 *
 * Behaviour:
 * - Maintains local input state (`inputVrn`) and validates required/max length.
 * - Displays validation errors inline via `RegistrationInput`.
 * - On confirm, calls `onUpdateVrn` and closes the modal.
 * - Uses i18n for display text (header/buttons/placeholder).
 */

import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import RegistrationInput from './RegistrationInput';
import { ButtonCommand } from './Button/ButtonCommand';
import { ButtonConfirm } from './Button/ButtonConfirm';
import { CommonValidation } from './CommonValidation/commonValidation';
import { useTranslation } from 'react-i18next';

type VrnInputModalProps = {
  vrn: string;
  showModal: boolean;
  onUpdateVrn: (vrn: string) => void;
  onCloseModal: () => void;
};

const VrnInputModal = ({
  vrn,
  showModal,
  onUpdateVrn,
  onCloseModal
}: VrnInputModalProps): JSX.Element => {
  const [vrnError, setVrnError] = useState('');
  const [inputVrn, setInputVrn] = useState(vrn);
  const [showVrnModal, setShowVrnModal] = useState<boolean>(showModal);

  const onChangeVehicleReg = (e: React.FormEvent<HTMLInputElement>) => {
    setInputVrn(e.currentTarget.value);
  };

  const { t } = useTranslation();

  const isVrnValid = () => {
    let isValid = true;

    const vrnCharLimit = 10;

    if (CommonValidation.isEmpty(inputVrn)) {
      setVrnError('To proceed, please enter a customer vehicle registration');
      isValid = false;
    } else {
      if (!CommonValidation.isValidLength(inputVrn, vrnCharLimit)) {
        setVrnError(`Too many characters. Max length (${vrnCharLimit})`);
        isValid = false;
      }
    }

    if (isValid) {
      setVrnError('');
    }

    return isValid;
  };

  const onVrnConfirm = (): void => {
    if (isVrnValid()) {
      onUpdateVrn(inputVrn);
      setShowVrnModal(false);
    }
  };

  return (
    <Modal isOpen={showVrnModal}>
      <ModalHeader toggle={onCloseModal}>
        {t('customer-product-page.vrn-modal.enter-vehicle-registration')}
      </ModalHeader>
      <ModalBody>
        <RegistrationInput
          name='vehicle-registration-number'
          className='input-text'
          placeholder={t(
            'customer-product-page.vrn-modal.vehicle-registration-placeholder'
          )}
          value={inputVrn}
          error={vrnError}
          onChange={(e: React.FormEvent<HTMLInputElement>) =>
            onChangeVehicleReg(e)
          }
        />
      </ModalBody>
      <ModalFooter>
        <div className='flex-row button-row'>
          <ButtonCommand
            name='btn-close-confirmation'
            text={t('customer-button.button-cancel-text')}
            onClick={onCloseModal}
          />
          <ButtonConfirm
            name='btn-confirm-manual-vehicle'
            text={t('customer-button.button-confirm-text')}
            additionalClassNames='m05-left'
            onClick={onVrnConfirm}
          />
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default VrnInputModal;
